import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRight,
  BookOpen,
  Timer,
  Check,
  Focus,
  Sparkles,
  RefreshCcw,
  GraduationCap,
  Smartphone,
  Layers,
  ChevronRight,
  Home,
  Clock,
  Lock,
  Star,
} from "lucide-react";

/* ============ Shared reveal ============ */
const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px", ...options }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

/* ============================================================
   1. HERO
   ============================================================ */
const HeroFloatingCard = ({ icon: Icon, title, pos, delay, inView, accent }) => {
  const style = {
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0, 20px, 0) scale(0.9)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: "transform, opacity",
  };
  return (
    <div className={`absolute ${pos} z-10 medily-float-slow`} style={style}>
      <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-white px-3.5 py-2.5 shadow-[0_25px_55px_-15px_rgba(29,78,216,0.4)] backdrop-blur">
        <span className={`w-8 h-8 rounded-lg bg-[#eef4ff] flex items-center justify-center ${accent || "text-[#1d4ed8]"}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[12.5px] font-semibold text-slate-800 whitespace-nowrap">{title}</span>
      </div>
    </div>
  );
};

const HeroVisual = ({ inView }) => {
  const laptopStyle = {
    transform: inView
      ? "translate3d(0,0,0) rotate(0) scale(1)"
      : "translate3d(-60px, 24px, 0) rotate(-2deg) scale(0.97)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 120ms",
    willChange: "transform, opacity",
  };
  const phoneStyle = {
    transform: inView
      ? "translate3d(0,0,0) rotate(0) scale(1)"
      : "translate3d(-40px, 32px, 0) rotate(3deg) scale(0.94)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 1300ms cubic-bezier(0.22, 1, 0.36, 1) 320ms, opacity 1000ms cubic-bezier(0.22, 1, 0.36, 1) 320ms",
    willChange: "transform, opacity",
  };
  return (
    <div className="relative w-full" style={{ perspective: "1200px" }}>
      <div className="absolute -inset-8 -z-10 rounded-[52px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95" />
      <div className="absolute inset-x-8 -bottom-4 -z-10 h-10 rounded-full bg-[#1d4ed8]/30 blur-2xl" />
      <img
        src="/homepage/Learn 1 L.png"
        alt="Learning Hub on laptop"
        style={laptopStyle}
        className="w-full h-auto drop-shadow-[0_50px_80px_rgba(29,78,216,0.28)] select-none"
        draggable="false"
        data-testid="guides-hero-laptop"
      />
      <img
        src="/homepage/Learn 1 P.png"
        alt="Capacity topic on phone"
        style={phoneStyle}
        className="absolute -bottom-[2%] left-[-3%] md:left-[-6%] w-[36%] md:w-[32%] h-auto drop-shadow-[0_40px_75px_rgba(29,78,216,0.45)] select-none"
        draggable="false"
        data-testid="guides-hero-phone"
      />
      <HeroFloatingCard icon={BookOpen} title="75+ Expert Resources" pos="top-4 -right-4 md:-right-8" delay={520} inView={inView} />
      <HeroFloatingCard icon={Timer} title="1–3 Minute Topics" pos="top-1/2 -right-3 md:-right-10" delay={660} inView={inView} />
      <HeroFloatingCard icon={Check} title="Progress Saved" pos="bottom-8 -right-4 md:-right-6" delay={800} inView={inView} accent="text-[#15803d]" />
    </div>
  );
};

const Hero = () => {
  const { ref, inView } = useReveal();
  const copyStyle = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 28px, 0)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="guides-hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] bg-[#bcd3ff] rounded-full blur-3xl opacity-90" />
        <div className="absolute top-24 -right-40 w-[640px] h-[640px] bg-[#d5e4ff] rounded-full blur-3xl opacity-95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.65),transparent_60%)]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 pt-20 md:pt-24 pb-28 md:pb-36 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div style={copyStyle}>
          <span className="inline-block px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-[#dbe6ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.14em] uppercase shadow-sm">
            Our Guides
          </span>
          <h1
            className="mt-6 font-semibold text-slate-900 leading-[1.02] tracking-tight text-[46px] md:text-[64px]"
            data-testid="guides-hero-heading"
          >
            Master{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span className="relative z-10 text-[#1d4ed8]">what matters</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
              <span className="relative z-10">.</span>
            </span>
          </h1>
          <p className="mt-6 text-slate-500 text-[17px] leading-relaxed max-w-[560px]">
            Explore our complete collection of focused, expert-written guides designed to help you
            build the knowledge, confidence and interview technique needed for your MMI.
          </p>
        </div>
        <div>
          <HeroVisual inView={inView} />
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   2. GUIDE SHOWCASE
   ============================================================ */
const GUIDES = [
  {
    id: "ethics",
    label: "Pillar · Guide 01",
    title: "Medical Ethics Guide",
    intro:
      "Understand the ethical principles, professional responsibilities and legal frameworks that shape modern healthcare. Learn how to approach ethical MMI scenarios with balanced reasoning and confidence.",
    bullets: [
      "The Four Pillars of Medical Ethics",
      "Capacity",
      "Consent",
      "Gillick Competence & Fraser Guidelines",
      "Confidentiality",
    ],
    more:
      "Plus Duty of Candour, Safeguarding, Raising Concerns and many more topics on medical ethics.",
    book: "/guides/book-ethics.png",
    glow: "from-[#c9dcff] via-[#dbe6ff]", // blue
    shadowRgba: "29,78,216",
  },
  {
    id: "cases",
    label: "Pillar · Guide 02",
    title: "Medical Case Studies Guide",
    intro:
      "Explore important UK healthcare cases and landmark reports that regularly appear in MMI discussions, understanding what happened, why it matters and the lessons future doctors should take away.",
    bullets: [
      "Lord Darzi Report",
      "Francis Reports",
      "Martha’s Rule",
      "Max and Keira’s Law",
      "Dr Bawa-Garba Case",
    ],
    more:
      "Plus Harold Shipman, Lucy Letby, Charlie Gard and many more medical case studies.",
    book: "/guides/book-cases.png",
    glow: "from-[#c9ece5] via-[#dcf3ee]", // teal
    shadowRgba: "13,148,136",
  },
  {
    id: "techniques",
    label: "Pillar · Guide 03",
    title: "Station Techniques Guide",
    intro:
      "Learn how different MMI stations work, what interviewers are assessing and practical frameworks for approaching every station confidently.",
    bullets: [
      "Understanding the MMI",
      "Personal Statement Station",
      "Motivation for Medicine",
      "Ethical Decision-Making",
      "Role Play Stations",
    ],
    more:
      "Plus Breaking Bad News, Teamwork Stations, Explaining Medical Information and many more MMI station techniques.",
    book: "/guides/book-techniques.png",
    glow: "from-[#dcd0f5] via-[#eae0f9]", // lavender
    shadowRgba: "139,92,246",
  },
  {
    id: "constitution",
    label: "Pillar · Guide 04",
    title: "NHS Constitution Guide",
    intro:
      "Develop a clear understanding of how the NHS works, the principles behind it and the values expected of every future doctor.",
    bullets: [
      "History of the NHS",
      "NHS Constitution",
      "Seven Principles of the NHS",
      "NHS Core Values",
      "Primary, Secondary & Tertiary Care",
    ],
    more:
      "Plus MDTs, Integrated Care Systems, NHS England and many more resources on the NHS Constitution.",
    book: "/guides/book-constitution.png",
    glow: "from-[#fddcbd] via-[#fee9d2]", // orange
    shadowRgba: "234,88,12",
  },
  {
    id: "hot-topics",
    label: "Pillar · Guide 05",
    title: "NHS Hot Topics Guide",
    intro:
      "Stay informed on the latest issues affecting UK healthcare so you can confidently discuss current challenges during your interviews.",
    bullets: [
      "NHS Waiting Lists",
      "The 7-Day NHS",
      "NHS Postcode Lottery",
      "Winter Pressures",
      "Racism & Equality",
    ],
    more:
      "Plus Workforce Shortages, Social Care Crisis, Ageing Population and many more NHS Hot Topics.",
    book: "/guides/book-hot-topics.png",
    glow: "from-[#fbeeb5] via-[#fdf4cf]", // yellow
    shadowRgba: "202,138,4",
  },
  {
    id: "tips",
    label: "Pillar · Guide 06",
    title: "Interview Tips Guide",
    intro:
      "Master the practical skills that separate average applicants from outstanding candidates, from structuring answers to staying calm under pressure.",
    bullets: [
      "How Interviewers Actually Mark You",
      "Buying Time",
      "What If You Don’t Know the Answer?",
      "What If Your Mind Goes Blank?",
      "Body Language & First Impressions",
    ],
    more: null,
    footer: {
      lead: "Plus Time Management, Difficult Questions, Structuring Answers and",
      highlight: "Medily’s Golden Rules",
      trail: ".",
    },
    book: "/guides/book-tips.png",
    glow: "from-[#bfeaef] via-[#d6f2f5]", // cyan
    shadowRgba: "8,145,178",
  },
];

const BookVisual = ({ book, glow, shadowRgba, fromLeft, inView }) => {
  const bookStyle = {
    transform: inView
      ? "translate3d(0,0,0) rotate(-4deg) scale(1)"
      : `translate3d(${fromLeft ? "-40px" : "40px"}, 20px, 0) rotate(${fromLeft ? "-2deg" : "2deg"}) scale(0.94)`,
    opacity: inView ? 1 : 0,
    transition:
      "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 180ms, opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 180ms",
    willChange: "transform, opacity",
  };
  return (
    <div className="relative flex justify-center items-center min-h-[300px]">
      <div
        className={`absolute inset-0 -z-10 rounded-full blur-3xl bg-gradient-to-br ${glow} to-transparent opacity-90 scale-90`}
      />
      <img
        src={book}
        alt=""
        style={{
          ...bookStyle,
          filter: `drop-shadow(0 45px 80px rgba(${shadowRgba}, 0.4))`,
        }}
        className="medily-book-float w-[240px] md:w-[320px] h-auto select-none hover:-translate-y-1 transition-transform duration-500"
        onError={(e) => {
          e.currentTarget.style.display = "none";
          const ph = e.currentTarget.nextSibling;
          if (ph) ph.style.display = "flex";
        }}
        draggable="false"
        data-testid="guide-book-img"
      />
      {/* Fallback placeholder — hidden by default; shown if book image fails */}
      <div
        style={{ display: "none", ...bookStyle }}
        className="medily-book-float w-[240px] h-[340px] rounded-[14px] bg-gradient-to-br from-white to-[#eef4ff] shadow-[0_35px_60px_-15px_rgba(29,78,216,0.45)] border border-slate-100 flex-col p-6"
      >
        <div className="text-[11px] font-semibold text-[#1d4ed8]">MEDILY</div>
        <div className="mt-auto text-[11px] text-slate-400">Awaiting cover</div>
      </div>
    </div>
  );
};

const GuideShowcase = ({ guide, index }) => {
  const { ref, inView } = useReveal();
  const bookLeft = index % 2 === 1; // 0=text-left, 1=book-left, 2=text-left, ...
  const copyStyle = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 32px, 0)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section
      ref={ref}
      className="relative w-full py-20 md:py-28"
      data-testid={`guide-${guide.id}-section`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className={`${bookLeft ? "lg:order-1" : "lg:order-2"} order-1`}>
            <BookVisual
              book={guide.book}
              glow={guide.glow}
              shadowRgba={guide.shadowRgba}
              fromLeft={bookLeft}
              inView={inView}
            />
          </div>
          <div className={`${bookLeft ? "lg:order-2" : "lg:order-1"} order-2`} style={copyStyle}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.14em] uppercase">
              <BookOpen className="w-3.5 h-3.5" /> {guide.label}
            </span>
            <h3
              className="mt-5 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.06]"
              data-testid={`guide-${guide.id}-title`}
            >
              {guide.title}
              <span className="text-[#1d4ed8]">.</span>
            </h3>
            <p className="mt-5 text-slate-600 text-[16px] leading-relaxed max-w-[560px]">
              {guide.intro}
            </p>
            <div className="mt-7">
              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
                Inside this guide
              </p>
              <ul className="mt-3 space-y-2.5 max-w-[560px]">
                {guide.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 w-6 h-6 shrink-0 rounded-full bg-[#1d4ed8]/10 text-[#1d4ed8] flex items-center justify-center">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-[15px] text-slate-700 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            {guide.more && (
              <p className="mt-6 text-[14.5px] text-slate-500 leading-relaxed italic max-w-[560px]">
                {guide.more}
              </p>
            )}
            {guide.footer && (
              <p className="mt-6 text-[14.5px] text-slate-500 leading-relaxed italic max-w-[560px]">
                {guide.footer.lead}{" "}
                <span className="inline-flex items-center gap-1 not-italic font-semibold text-[#1d4ed8]">
                  <Star className="w-3.5 h-3.5 fill-current" />
                  {guide.footer.highlight}
                </span>
                {guide.footer.trail}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const GuideShowcaseSection = () => (
  <div data-testid="guides-showcase-container">
    {GUIDES.map((g, i) => (
      <GuideShowcase key={g.id} guide={g} index={i} />
    ))}
  </div>
);

/* ============================================================
   3. DESIGNED FOR MODERN MMI PREPARATION
   ============================================================ */
const MODERN_CARDS = [
  { id: "focused", icon: Focus, title: "Focused", body: "Only the concepts most relevant to UK MMIs." },
  { id: "concise", icon: Timer, title: "Concise", body: "Most topics take between 1 and 3 minutes to read." },
  { id: "up-to-date", icon: RefreshCcw, title: "Up to Date", body: "Resources are regularly reviewed to reflect current healthcare developments and interview expectations." },
  { id: "university", icon: GraduationCap, title: "University-Specific", body: "Content is designed around the skills and themes commonly assessed by UK medical schools." },
  { id: "accessible", icon: Smartphone, title: "Accessible Anywhere", body: "Study across desktop, tablet and mobile whenever it suits you." },
  { id: "together", icon: Layers, title: "Built to Work Together", body: "Move from learning a topic to practising it with Maya and applying it in a mock interview." },
];

const ModernCard = ({ item, i }) => {
  const { ref, inView } = useReveal();
  const Icon = item.icon;
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 22px, 0)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 90}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 90}ms`,
    willChange: "transform, opacity",
  };
  return (
    <div
      ref={ref}
      style={style}
      className="rounded-3xl bg-white border border-slate-100 p-6 md:p-7 shadow-[0_20px_50px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_30px_70px_-25px_rgba(29,78,216,0.5)] hover:-translate-y-1 transition-all duration-500"
      data-testid={`modern-card-${item.id}`}
    >
      <span className="inline-flex w-11 h-11 rounded-2xl bg-[#eaf1ff] text-[#1d4ed8] items-center justify-center">
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <h4 className="mt-5 text-[18px] font-semibold text-slate-900 tracking-tight">{item.title}</h4>
      <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{item.body}</p>
    </div>
  );
};

const DesignedFor = () => (
  <section className="max-w-6xl mx-auto px-6 pt-24 md:pt-32 pb-16" data-testid="guides-modern-section">
    <div className="text-center max-w-2xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">
        Purpose-built
      </span>
      <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
        Designed for{" "}
        <span className="relative inline-block">
          <span className="relative z-10 text-[#1d4ed8]">modern MMI preparation</span>
          <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
        </span>
        .
      </h2>
      <p className="mt-4 text-slate-500 text-[16px] leading-relaxed">
        Every resource is built to help you study efficiently, stay current and focus on the
        concepts that genuinely shape strong MMI answers.
      </p>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {MODERN_CARDS.map((c, i) => (
        <ModernCard key={c.id} item={c} i={i} />
      ))}
    </div>
  </section>
);

/* ============================================================
   4. PREVIEW A RESOURCE — Capacity (locked at MCA 2005)
   ============================================================ */
const ResourcePreview = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 pt-24 pb-24" data-testid="guides-preview-section">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">
          Try it yourself
        </span>
        <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          See exactly how you’ll learn.
        </h2>
        <p className="mt-4 text-slate-500 text-[16px] leading-relaxed">
          Explore a preview of our <span className="text-[#1d4ed8] font-medium">Capacity</span>{" "}
          resource from the Medical Ethics Guide.
        </p>
      </div>

      {/* Portal preview */}
      <div className="mt-12 relative rounded-[28px] overflow-hidden border border-slate-100 bg-white shadow-[0_45px_110px_-30px_rgba(29,78,216,0.5)]" data-testid="guides-preview-frame">
        {/* Fake app chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50/70 pointer-events-none select-none">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-[11px] text-slate-400">medily.co.uk / resources / medical-ethics / capacity</span>
        </div>

        <div className="grid md:grid-cols-[220px_1fr]">
          {/* Sidebar (view-only) */}
          <aside className="hidden md:flex flex-col gap-1 border-r border-slate-100 bg-[#f7faff]/60 p-4 pointer-events-none select-none" aria-hidden>
            <div className="text-[11px] font-semibold text-[#1d4ed8] tracking-[0.14em] uppercase">Medily</div>
            {["Dashboard", "Study Resources", "Interview Tips", "Maya", "Mock MMI", "Progress"].map((l, i) => (
              <div
                key={i}
                className={`text-[12.5px] px-3 py-2 rounded-lg ${i === 1 ? "bg-white text-[#1d4ed8] shadow-sm" : "text-slate-500"}`}
              >
                {l}
              </div>
            ))}
            <div className="mt-4 text-[11px] font-semibold text-slate-400 uppercase tracking-[0.14em]">Ethics Guide</div>
            {["Four Pillars", "Capacity", "Consent", "Confidentiality", "Gillick & Fraser"].map((l, i) => (
              <div
                key={i}
                className={`text-[12.5px] px-3 py-2 rounded-lg ${i === 1 ? "bg-[#1d4ed8]/10 text-[#1d4ed8] font-semibold" : "text-slate-500"}`}
              >
                {l}
              </div>
            ))}
          </aside>

          {/* Article — scrollable up to MCA, then locked */}
          <div className="relative pointer-events-none select-none" data-testid="guides-preview-article">
            <div className="p-6 md:p-10 pb-40">
              {/* Breadcrumbs */}
              <div className="flex items-center gap-1.5 text-[12px] text-slate-400" aria-hidden>
                <Home className="w-3.5 h-3.5" />
                <ChevronRight className="w-3.5 h-3.5" />
                <span>Resources</span>
                <ChevronRight className="w-3.5 h-3.5" />
                <span>Medical Ethics</span>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-[#1d4ed8] font-medium">Capacity</span>
              </div>

              {/* Heading */}
              <h3 className="mt-4 text-[36px] md:text-[44px] font-semibold text-slate-900 tracking-tight leading-tight">
                Capacity
              </h3>
              <div className="mt-3 flex items-center gap-3 text-[12.5px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 3 min read</span>
                <span className="text-slate-300">·</span>
                <span>Medical Ethics Guide</span>
              </div>

              {/* Progress card */}
              <div className="mt-6 rounded-2xl border border-slate-100 bg-gradient-to-br from-[#eef4ff] to-white p-5 flex items-center justify-between">
                <div>
                  <p className="text-[12px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Your progress</p>
                  <p className="mt-1 text-[14px] text-slate-800 font-medium">Topic 2 of 12 · Medical Ethics</p>
                </div>
                <div className="w-40">
                  <div className="h-2 rounded-full bg-white overflow-hidden shadow-inner">
                    <div className="h-full w-[16%] bg-gradient-to-r from-[#1d4ed8] to-[#4f7cff]" />
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500 text-right">16%</p>
                </div>
              </div>

              {/* Topic Introduction */}
              <div className="mt-10">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#1d4ed8] font-semibold">Topic Introduction</p>
                <p className="mt-3 text-[16px] text-slate-700 leading-[1.75]">
                  Capacity refers to a person’s ability to make a specific decision at a specific
                  time. In UK healthcare, respecting a patient’s capacity is fundamental to
                  autonomous, dignified and lawful medical practice. Understanding capacity is
                  central to consent, confidentiality and safeguarding — and it is one of the
                  ethical concepts MMI panels most commonly test.
                </p>
                <p className="mt-4 text-[16px] text-slate-700 leading-[1.75]">
                  Doctors must be able to assess whether a patient can understand information, weigh
                  it against their own values and communicate a decision. A capacity assessment is
                  always decision-specific and time-specific — a patient may have capacity for one
                  choice but not another.
                </p>
              </div>

              {/* Key Concepts */}
              <div className="mt-10">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#1d4ed8] font-semibold">Key Concepts</p>
                <ul className="mt-4 space-y-3">
                  {[
                    ["Autonomy", "Adults with capacity have the right to make decisions about their own care, even if others disagree with those decisions."],
                    ["Decision-specific", "Capacity is assessed for a particular decision at a particular moment — never a global label."],
                    ["Presumption of capacity", "Every adult is presumed to have capacity unless proven otherwise."],
                    ["Support before assessment", "All practicable steps must be taken to help someone make their own decision before concluding they lack capacity."],
                  ].map(([term, desc], i) => (
                    <li key={i} className="rounded-2xl border border-slate-100 p-4 bg-white">
                      <p className="text-[14.5px] font-semibold text-slate-900">{term}</p>
                      <p className="mt-1 text-[14px] text-slate-600 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mental Capacity Act 2005 (MCA) — the paywall boundary */}
              <div className="mt-10" data-testid="guides-preview-mca-anchor">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#1d4ed8] font-semibold">Mental Capacity Act 2005 (MCA)</p>
                <p className="mt-3 text-[16px] text-slate-700 leading-[1.75]">
                  The Mental Capacity Act 2005 is the foundational legislation that governs how
                  capacity is assessed and how decisions are made for people who cannot decide for
                  themselves in England and Wales. It sets out five statutory principles that any
                  clinician must apply…
                </p>
                {/* additional teaser content that gets blurred */}
                <ul className="mt-4 space-y-2 text-[15px] text-slate-600 leading-relaxed">
                  <li>Principle 1 · Presume capacity.</li>
                  <li>Principle 2 · Support the person to decide.</li>
                  <li>Principle 3 · Unwise decisions are still valid decisions.</li>
                  <li>Principle 4 · Act in the person’s best interests.</li>
                  <li>Principle 5 · Choose the least restrictive option.</li>
                </ul>
                <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
                  The two-stage test of capacity: (1) does the person have an impairment of, or a
                  disturbance in, the functioning of the mind or brain? (2) if so, does that
                  impairment mean the person is unable to make the specific decision when they need
                  to?
                </p>
              </div>
            </div>

            {/* Blur + gate overlay — sits over the bottom half of the article */}
            <div
              className="absolute inset-x-0 bottom-0 h-[62%] pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 22%, rgba(255,255,255,0.92) 55%, #ffffff 100%)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                maskImage:
                  "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 32%, rgba(0,0,0,1) 100%)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 32%, rgba(0,0,0,1) 100%)",
              }}
              aria-hidden
              data-testid="guides-preview-blur"
            />

            {/* Gate content */}
            <div className="absolute inset-x-0 bottom-0 pb-10 md:pb-16 flex flex-col items-center justify-end text-center px-6 pointer-events-auto">
              <div className="max-w-[520px]">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 text-[#1d4ed8] text-[11px] font-semibold uppercase tracking-[0.14em] shadow-sm border border-[#dbe6ff]">
                  <Lock className="w-3 h-3" />
                  Full topic locked
                </span>
                <h4 className="mt-4 text-[26px] md:text-[32px] font-semibold text-slate-900 tracking-tight leading-tight">
                  Ready to continue learning?
                </h4>
                <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">
                  Unlock the full Capacity resource and 75+ expert-written MMI topics with Medily.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/pricing")}
                  className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_40px_-12px_rgba(29,78,216,0.65)]"
                  data-testid="guides-preview-unlock-btn"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   5. FINAL CTA
   ============================================================ */
const FinalCta = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="guides-final-cta-section">
      <div className="rounded-[32px] bg-gradient-to-br from-[#eef4ff] via-white to-[#dbe6ff] border border-white p-12 md:p-20 text-center shadow-[0_45px_110px_-30px_rgba(29,78,216,0.55)]">
        <h3 className="text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          Start preparing with{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#1d4ed8]">confidence</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>
          .
        </h3>
        <p className="mt-5 text-slate-600 max-w-2xl mx-auto text-[16.5px] leading-relaxed">
          Build your knowledge with concise, up-to-date resources designed around the demands of UK
          medical school interviews.
        </p>
        <div className="mt-9 flex justify-center">
          <button
            onClick={() => navigate("/pricing")}
            className="px-8 py-4 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_45px_-12px_rgba(29,78,216,0.6)] inline-flex items-center gap-2"
            data-testid="guides-final-cta-btn"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   PAGE
   ============================================================ */
const OurGuides = () => (
  <div className="bg-white min-h-screen">
    <Navbar />
    <Hero />
    <GuideShowcaseSection />
    <DesignedFor />
    <ResourcePreview />
    <FinalCta />
    <Footer />
  </div>
);

export default OurGuides;
